import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { makeAuthedRequest } from "../utils/makeAuthedRequest";
import { parseApiError } from "../utils/apiError";
import env from "../environment";

export function useIBO() {
  // Profile is { users: [...], isFreeTrial }; IBO is injected by UserProfileGet BFF into users[0]
  const [ibo, setIbo] = useState(null);
  useState(() => {
    makeAuthedRequest("/user/profile")
      .then((r) => r.json())
      .then((data) => {
        const users = Array.isArray(data?.users)
          ? data.users
          : Array.isArray(data)
          ? data
          : [data];
        setIbo(users[0]?.iboNumber ?? null);
      })
      .catch(() => {});
  });
  return ibo;
}

// ─── Step 0 ──────────────────────────────────────────────────────────────────

function configureCollectJS(callback, onFieldValidation) {
  // Collect.js applies these by setting element.style[key] = value inside the
  // field iframe, so keys must be camelCase JS style properties (not kebab-case
  // CSS) and values can't use "!important" (that requires setProperty's 3rd
  // arg, which Collect.js doesn't use — the whole value is otherwise ignored).
  const fieldCss = {
    border: "none",
    outline: "none",
    boxShadow: "none",
    appearance: "none",
    WebkitAppearance: "none",
    height: "100%",
    padding: "0",
    fontSize: "13px",
    fontFamily: "Open Sans",
    color: "#415364",
  };

  window.CollectJS.configure({
    variant: "inline",
    styleSniffer: false,
    primaryColor: "#91B6BB",
    googleFont: "Open Sans:400",
    paymentType: "cc",
    // Collect.js can auto-bind tokenization to a page button (default id
    // "payButton") so it fires on its own click listener instead of ours.
    // We trigger tokenization ourselves via startPaymentRequest(), so make
    // sure nothing else can trigger it.
    paymentSelector: false,
    customCss: fieldCss,
    invalidCss: fieldCss,
    validCss: fieldCss,
    focusCss: fieldCss,
    fields: {
      cvv: "hide",
      ccnumber: { selector: "#ccnumber", placeholder: "Credit Card Number" },
      ccexp: { selector: "#ccexp", placeholder: "MM/YY" },
    },
    callback,
    validationCallback(field, status) {
      onFieldValidation?.(field, status);
    },
  });
}

// Fetches the glacier tokenization key from core, injects Collect.js, then
// calls configureCollectJS so iframes are ready before the user submits.
export function useGlacierKey({ onToken, onFieldValidation } = {}) {
  const [isReady, setIsReady] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    ccnumber: null,
    ccexp: null,
  });

  const load = useMutation(
    async () => {
      // Collect.js tokenization key lives in the settings BFF, not core
      const settingsBase = env.coreApiBase.replace("/core", "/settings");
      const res = await makeAuthedRequest(
        "key",
        { method: "GET" },
        settingsBase
      );
      if (!res.ok) throw await parseApiError(res, "GlacierKeyGet failed");
      const { key: glacierKey } = await res.json();

      await new Promise((resolve, reject) => {
        const existing = document.querySelector(
          "script[data-tokenization-key]"
        );
        if (existing) existing.remove();

        const script = document.createElement("script");
        script.src = "https://secure.glacierpaygateway.com/token/Collect.js";
        script.setAttribute("data-tokenization-key", glacierKey);
        script.async = false;
        script.charset = "utf-8";
        script.onload = () => {
          if (!window.CollectJS) {
            reject(
              new Error(
                "CollectJS failed to initialize — check the tokenization key"
              )
            );
            return;
          }
          try {
            configureCollectJS(
              (response) => onToken?.(response.token),
              (field, status) => {
                setFieldErrors((prev) => ({
                  ...prev,
                  [field]: status ? null : `Invalid ${field}`,
                }));
                onFieldValidation?.(field, status);
              }
            );
          } catch (err) {
            // configure() throws synchronously inside this onload callback,
            // outside the executor's try/catch scope, so without this it
            // would be an uncaught exception and this promise would hang
            // forever, freezing isReady/isSubmitting in a stuck state.
            reject(err);
            return;
          }
          setIsReady(true);
          resolve();
        };
        script.onerror = reject;
        document.body.appendChild(script);
      });
    },
    {
      // CardPaymentModel renders this failure inline on the card form.
      meta: { suppressErrorModal: true },
    }
  );

  const startPayment = () => window.CollectJS?.startPaymentRequest?.();

  return { load, isReady, fieldErrors, startPayment };
}

// ─── Step 1 ──────────────────────────────────────────────────────────────────

// Sends the Collect.js token to core AddCustomerPost and returns enriched
// card data: cc_exp, cc_bin, lastFour, customer_vault_id, cardType, etc.
export function useAddCustomer() {
  const ibo = useIBO();
  return useMutation(
    async ({ paymentToken, postalCode, nickname = "", isPrimary = false }) => {
      const res = await makeAuthedRequest(
        "billing/glacier/add-customer",
        {
          method: "POST",
          body: JSON.stringify({
            ibo,
            isPrimary,
            nickname,
            glacierPaymentToken: { paymentToken, postalCode },
          }),
        },
        env.coreApiBase
      );
      if (!res.ok) throw await parseApiError(res, "AddCustomerPost failed");
      return res.json();
    },
    // CardPaymentModel renders this failure inline on the card form.
    { meta: { suppressErrorModal: true } }
  );
}

// ─── Step 2 (wired after step 1 is confirmed) ────────────────────────────────

// Sends enriched card data from AddCustomerPost to core AddCardPost to
// persist the card record to the membership DB.
export function useAddCardToMembership() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      ibo,
      customerData,
      postalCode,
      nickname = "",
      isPrimary = false,
    }) => {
      const payload = {
        ibo,
        cardHolder: `${customerData.first_name ?? ""} ${
          customerData.last_name ?? ""
        }`.trim(),
        expiration: customerData.cc_exp,
        firstSix: customerData.cc_bin,
        isPrimary,
        lastFour: customerData.lastFour,
        nickname,
        postalCode,
        token: customerData.customer_vault_id,
      };

      const res = await makeAuthedRequest(
        "billing/glacier/add-card",
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
        env.coreApiBase
      );
      if (!res.ok) throw await parseApiError(res, "AddCardPost failed");
      return res.json();
    },
    {
      onSuccess: () => queryClient.invalidateQueries("paymentMethods"),
      // CardPaymentModel renders this failure inline on the card form.
      meta: { suppressErrorModal: true },
    }
  );
}
